import type {
  CarePlanSuggestion,
  HraSummaryResponse,
  MemberHra,
  RecommendationReview,
  SuggestionStatus,
} from '@troyai/hra/data-access';
import { DEMO_ACTORS, demoMemberId, storyDate, storyIso } from '@troyai/portal/investor-demo/core';

import type { DemoData } from '../demo-data';
import { suggestedContent, suggestedMeta } from './care-plan-templates.fixture';
import { ASSESSMENT_ID, suggestionId, templateId } from './ids';

/**
 * The HRA submitted Sep 24 10:02 and its 12 recommendations. Rows 1 to 4 were approved and became
 * the four episodes; rows 5 to 7 were approved without a care plan; row 8 was rejected; rows 9 to
 * 12 are the four still to review. Reviewers are emails, as the backend stores them.
 *
 * Dates in comments are the scripted ones, written for Tue Oct 13; the data and the sentences move
 * them onto the real story day (`storyIso`, `storyDate`).
 */

const DANA = DEMO_ACTORS.dana.email;
const LISA = DEMO_ACTORS.lisa.email;

const SUBMITTED_AT = storyIso('2026-09-24T10:02:00');
const GENERATED_AT = storyIso('2026-09-24T10:10:00');

interface Review {
  status: Exclude<SuggestionStatus, 'suggested'>;
  by: string;
  at: string;
  reason?: string;
}

interface SuggestionRow {
  /** The row of the spec's table, 1 to 12. */
  row: number;
  /** The recommendation text. */
  name: string;
  /** The "why": names the timeline evidence behind it. */
  rationale: string;
  /** Absent while it is still to review. */
  review?: Review;
  createdAt?: string;
}

const ROWS: SuggestionRow[] = [
  {
    row: 1,
    name: 'CHF medication review: stop meloxicam (NSAID with CHF and CKD 3a)',
    rationale: `Meloxicam, an NSAID, has been filled every month since ${storyDate('2026-06-12', 'MMM d')} (Dr. Patel, orthopedics), most recently ${storyDate('2026-09-16', 'MMM d')}. She has CHF (NYHA II) and CKD stage 3a, and went to the ED with fluid overload on ${storyDate('2026-08-19', 'MMM d')}. NSAIDs hold back fluid and strain the kidneys.`,
    review: { status: 'approved', by: LISA, at: storyIso('2026-10-06T16:05:00') },
  },
  {
    row: 2,
    name: 'Transportation support (ride benefit)',
    rationale: `Agent note from the ${storyDate('2026-09-24', 'MMM d')} HRA call: lost her car in June, lives alone, daughter in Raleigh. She has cardiology and orthopedics follow-ups ahead and no way to get to them.`,
    review: { status: 'approved', by: DANA, at: storyIso('2026-10-06T16:18:00') },
  },
  {
    row: 3,
    name: 'Annual diabetic eye exam',
    rationale:
      'Type 2 diabetes with an A1c of 8.4, and no eye exam on record this year. The Care signals list the exam as an open gap.',
    review: { status: 'approved', by: DANA, at: storyIso('2026-10-06T16:24:00') },
  },
  {
    row: 4,
    name: 'Pneumococcal vaccination (PCV20)',
    rationale: `No pneumococcal vaccine on record. At 70, with CHF, diabetes and CKD stage 3a, a pneumonia would be hard on her; the ED visit on ${storyDate('2026-08-19', 'MMM d')} shows how little reserve she has.`,
    review: { status: 'approved', by: LISA, at: storyIso('2026-10-13T08:42:00') },
    createdAt: storyIso('2026-10-06T10:30:00'),
  },
  {
    row: 5,
    name: 'Flu shot reminder',
    rationale:
      'No flu vaccine on record for this season. CHF and diabetes make influenza a risk for another admission.',
    review: { status: 'approved', by: DANA, at: storyIso('2026-09-25T09:40:00') },
  },
  {
    row: 6,
    name: 'Home blood pressure cuff',
    rationale:
      'Hypertension on lisinopril, metoprolol and amlodipine, plus furosemide for CHF, filled every month. No home readings in her record, so changes between visits go unseen.',
    review: { status: 'approved', by: DANA, at: storyIso('2026-09-25T09:41:00') },
  },
  {
    row: 7,
    name: 'Extra Help / benefits check',
    rationale:
      'Coverage shows no Extra Help. She lives alone, has been without a car since June, and fills nine medications every month.',
    review: { status: 'approved', by: DANA, at: storyIso('2026-09-25T09:44:00') },
  },
  {
    row: 8,
    name: 'Statin dose increase (atorvastatin 20 → 40 mg)',
    rationale: `Atorvastatin 20 mg fills have gaps (adherence 71%, last fill ${storyDate('2026-08-26', 'MMM d')}) in a patient with diabetes and CKD stage 3a.`,
    review: {
      status: 'rejected',
      by: LISA,
      at: storyIso('2026-10-06T15:52:00'),
      reason: 'Patient reported myalgia, rechallenge not appropriate.',
    },
  },
  {
    row: 9,
    name: 'Statin adherence outreach',
    rationale: `Atorvastatin adherence is 71%, below the 80% goal. The last fill was ${storyDate('2026-08-26', 'MMM d')} and her supply ran out on ${storyDate('2026-09-25', 'MMM d')}.`,
  },
  {
    row: 10,
    name: 'A1c recheck',
    rationale:
      'The last A1c on record is 8.4 and nothing since. Metformin and empagliflozin are filled every month, so a recheck would show whether they are enough.',
  },
  {
    row: 11,
    name: 'Fall-risk screen',
    rationale: `She lives alone with osteoarthritis and takes nine medications, including a diuretic and three blood pressure drugs. The ${storyDate('2026-08-19', 'MMM d')} ED visit was her most recent acute event.`,
  },
  {
    row: 12,
    name: 'PPI deprescribing review',
    rationale: `Omeprazole has been filled alongside meloxicam since ${storyDate('2026-06-12', 'MMM d')}. Once the NSAID stops, the need for a long-term PPI should be reviewed.`,
  },
];

function suggestion(spec: SuggestionRow): CarePlanSuggestion {
  const { review } = spec;
  return {
    id: suggestionId(spec.row),
    member_assessment_id: ASSESSMENT_ID,
    status: review?.status ?? 'suggested',
    suggested_template_id: templateId(spec.row),
    suggested_hra_meta: suggestedMeta(spec.row),
    rationale: spec.rationale,
    suggested_content: suggestedContent(spec.row),
    rejection_reason: review?.reason ?? '',
    reviewed_by: review?.by ?? '',
    reviewed_at: review?.at ?? null,
    created_care_plan_id: null,
    tenant_id: 1,
    created_at: spec.createdAt ?? GENERATED_AT,
    template_name: spec.name,
  };
}

const SUMMARY = `Margaret Hollis, 70, completed her health risk assessment by phone with the Troy Agent on ${storyDate('2026-09-24', 'MMM d')} (14 minutes). She lives alone in Charlotte, lost her car in June and has no way to get to appointments; her daughter lives in Raleigh. She manages heart failure, type 2 diabetes (A1c 8.4), hypertension and stage 3a kidney disease, and takes meloxicam for knee pain. Twelve care plan recommendations were generated from the call and her claims history.`;

/** The assessment, its summary, its 12 recommendations and the review counts (12, 4 to review, 3 approved without a care plan). */
export function seedHra(): Pick<
  DemoData,
  'assessments' | 'recommendationReview' | 'hraSummary' | 'suggestions'
> {
  const assessment: MemberHra = {
    id: ASSESSMENT_ID,
    type: 'hra',
    action_global_id: null,
    member_global_id: demoMemberId(),
    file_path_gcs: null,
    latest_file_id: null,
    submission_date: SUBMITTED_AT,
    submitted_by: DEMO_ACTORS.agent.email,
    last_modified_on: SUBMITTED_AT,
    addendums: [],
    service_date: storyIso('2026-09-24'),
    response_data: null,
    tenant_id: 1,
  };
  const recommendationReview: RecommendationReview = {
    member_assessment_id: ASSESSMENT_ID,
    assessment_type: 'hra',
    submitted_at: SUBMITTED_AT,
    recommendation_count: 12,
    to_review: 4,
    approved_without_care_plan: 3,
    last_generation_status: 'successful',
  };
  const hraSummary: HraSummaryResponse = { id: ASSESSMENT_ID, summary: SUMMARY };
  return {
    assessments: [assessment],
    recommendationReview,
    hraSummary,
    suggestions: ROWS.map(suggestion),
  };
}
